import { IGoogleFont } from './briefFontSelection.types'
import { useEffect, useState } from 'react'
import FontFaceObserver from 'fontfaceobserver'
import { LoaderIcon } from '@noissue-ui-kit/oldBranding/loaderIcon'
import { HiOutlineTrash } from 'react-icons/hi'

type TFontPreview = Pick<IGoogleFont, 'category' | 'family'> & {
  isVisible: boolean
  previewMode?: 'selectable' | 'selected'
  onSelection?: () => void
  onUnSelect?: () => void
  className?: string
  style?: Record<string, string>
}

export function FontPreview({
  category,
  family,
  isVisible,
  previewMode = 'selectable',
  onSelection,
  onUnSelect,
  className,
  style = null,
}: TFontPreview) {
  const [isFontLoaded, setFontIsLoaded] = useState(false)
  const [fontStyle, setFontStyle] = useState<string>(null)
  const [fontUrl, setFontUrl] = useState<string>(null)
  const [fontFetchinError, setFontFetchingError] = useState<string>(null)
  const fontStyleRule = {
    fontFamily: `${family}, ${category}`,
  }
  useEffect(() => {
    if (!fontUrl || !isVisible) {
      return
    }

    const getFontSyle = async () => {
      const dynamicFontStyles = await fetch(fontUrl).then((res) => res.text())
      setFontStyle(dynamicFontStyles)
      return dynamicFontStyles
    }

    getFontSyle().catch((err) => {
      setFontFetchingError(err.message)
    })

    const font = new FontFaceObserver(family, { weight: 400 })

    font
      .load(null, 10000)
      .then(() => setFontIsLoaded(true))
      .catch((err) => {
        setFontFetchingError(
          err.message ||
            'Taking too long to load. Please, try with better connection.'
        )
      })
  }, [fontUrl, isVisible])

  useEffect(() => {
    const fontFamilyName = family.split(' ').join('+')
    const fontCssLink = `https://fonts.googleapis.com/css?family=${fontFamilyName}:wght@400`
    setFontUrl(fontCssLink)
  }, [family])

  return (
    <div
      className={`relative flex flex-col p-6 text-core-grey-100 border-2 rounded-lg border-core-grey-30 bg-core-white ${className} ${
        previewMode === 'selectable'
          ? 'hover:bg-core-purple-10 hover:border-core-purple-20 hover:text-core-purple'
          : ''
      }`}
      onClick={previewMode === 'selectable' ? onSelection : null}
      style={style}
    >
      {!isFontLoaded && (
        <div className="absolute top-0 bottom-0 left-0 right-0 flex items-center justify-center opacity-75 bg-core-white">
          <LoaderIcon size="m" fillColorClass="fill-core-purple" />
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: fontStyle }} />

      <div className="flex items-center justify-between" style={fontStyleRule}>
        {!fontFetchinError ? (
          <div className="flex flex-col">
            <p
              className={`leading-none font-inherit ${
                previewMode === 'selectable' ? 'mb-3 text-4xl' : 'mb-0 text-2xl'
              }`}
            >
              {family}
            </p>

            {previewMode === 'selectable' && (
              <p className="max-w-xl text-xl font-inherit">
                The quick brown fox jumps over the sustainable noissue packaging
                box.
              </p>
            )}
          </div>
        ) : (
          <p className="text-xl text-center text-core-grey-100">
            {fontFetchinError}
          </p>
        )}
        {previewMode === 'selected' && (
          <HiOutlineTrash
            className="text-2xl cursor-pointer text-core-grey-70 hover:text-core-purple"
            onClick={onUnSelect}
          />
        )}
      </div>
    </div>
  )
}
